import { tr } from '../lib/tankRoleText';
import { useState } from 'react';
import { FolderPlus, FileUp } from 'lucide-react';
import { EPSG_NAMES } from '../lib/georef';
import { inpToProject } from '../lib/inpImport';

const IMT = {
  de: { sec: 'Abschnitt', n: 'Zeilen', st: 'Übernahme', ok: 'übernommen', part: 'teilweise (siehe Liste)', no: 'nicht übernommen – Ergebnis dazu nicht auswertbar', ign: 'ignoriert (nur Darstellung/Zeitsteuerung, ohne Einfluss auf das stationäre Ergebnis)' },
  en: { sec: 'Section', n: 'Rows', st: 'Taken over', ok: 'taken over', part: 'partly (see list)', no: 'not taken over – results on this cannot be evaluated', ign: 'ignored (display / time settings only, no effect on the steady-state result)' },
};
const IM = () => IMT[document.documentElement.lang] || IMT.en;
import { NetImport } from './NetImport';

const Opt = ({ id, on, set, label }) => <label className="row" style={{ gap: 6 }}><input type="radio" checked={on} onChange={set} data-testid={id} /> {label}</label>;
const go = (detail) => window.dispatchEvent(new CustomEvent('giss-aqua-demo', { detail }));
const center = () => window.__gissMapView?.()?.m?.getCenter() || { lat: 46.06, lng: 11.12 };

function Bg({ t, white, setWhite }) {
  return (
    <>
      <b>{t.aqDemoBg}</b>
      <Opt id="aq-demo-white" on={white} set={() => setWhite(true)} label={t.aqDemoWhite} />
      <Opt id="aq-demo-map" on={!white} set={() => setWhite(false)} label={t.aqDemoMap} />
    </>
  );
}

// own .inp file: placement at the map centre (scale / rotation) or real coordinates (EPSG), preview of what is not supported
function InpImport({ t, white }) {
  const [text, setText] = useState(null);
  const [pl, setPl] = useState({ mode: 'local', scale: '', rot: 0, epsg: 25832 });
  const prev = (() => { try { return text && inpToProject(text, { ...pl, center: center() }, t); } catch { return { error: true }; } })();
  const c = prev?.counts;
  return (
    <div className="aq-imp" data-testid="aq-imp">
      <b>{t.aqImpTitle}</b>
      <label className="btn btn-outline btn-sm" data-testid="aq-imp-file-btn"><FileUp size={14} /> {t.aqImpFile}<input type="file" accept=".inp,.txt" hidden data-testid="aq-imp-file" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setText(await f.text()); }} /></label>
      <b>{t.aqImpPlace}</b>
      <Opt id="aq-imp-local" on={pl.mode === 'local'} set={() => setPl({ ...pl, mode: 'local' })} label={t.aqImpLocal} />
      <Opt id="aq-imp-geo" on={pl.mode === 'geo'} set={() => setPl({ ...pl, mode: 'geo' })} label={t.aqImpGeo} />
      {pl.mode === 'local' ? <div className="aq-grid">
        <label>{t.aqImpScale}<input type="number" step="0.1" data-testid="aq-imp-scale" value={pl.scale} onChange={(e) => setPl({ ...pl, scale: e.target.value })} /></label>
        <label>{t.aqImpRot}<input type="number" step="1" data-testid="aq-imp-rot" value={pl.rot} onChange={(e) => setPl({ ...pl, rot: e.target.value })} /></label>
        <small className="muted aq-span">{t.aqImpCenterHint}</small>
      </div> : <label>{t.aqImpEpsg}<select data-testid="aq-imp-epsg" value={pl.epsg} onChange={(e) => setPl({ ...pl, epsg: +e.target.value })}>{Object.entries(EPSG_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>}
      {prev?.error && <p className="aq-err" data-testid="aq-imp-error">{t.aqImpErr}</p>}
      {c && <>
        <p data-testid="aq-imp-counts">{t.aqImpCounts.replace('{j}', c.j).replace('{t}', c.t).replace('{r}', c.r).replace('{p}', c.p).replace('{u}', c.u).replace('{v}', c.v).replace('{unit}', prev.unit)}</p>
        {prev.miss.length ? <div className="aq-dry" data-testid="aq-imp-miss"><b>{t.aqImpMiss} ({prev.miss.length})</b><ul>{prev.miss.slice(0, 40).map((m, i) => <li key={i}>{m}</li>)}</ul></div> : <p className="aq-ok" data-testid="aq-imp-allok">{t.aqImpAllOk}</p>}
        {prev.sections?.length > 0 && <table className="ni-dxf-tab" data-testid="aq-imp-matrix"><thead><tr><th>{IM().sec}</th><th>{IM().n}</th><th>{IM().st}</th></tr></thead><tbody>{prev.sections.map((s) => <tr key={s.k} data-testid={`aq-imp-sec-${s.k}`}><td>[{s.k}]</td><td>{s.n}</td><td className={`imp-${s.st}`}>{IM()[s.st]}</td></tr>)}</tbody></table>}
        <button className="btn btn-accent aq-run" data-testid="aq-imp-create-btn" onClick={() => go({ inp: text, place: pl, white })}><FolderPlus size={16} /> {t.aqImpCreate}</button>
      </>}
    </div>
  );
}

// Stormwater Management: sewer / heavy rain demo (SWMM), same project data as before the branch split
function StormDemo({ t, white, setWhite }) {
  return (
    <>
      <p className="muted">{t.swDemoIntro}</p>
      <b>{t.aqDemoWhich}</b>
      <Opt id="aq-demo-bozen-sew" on set={() => {}} label={t.sewDemo} />
      <small className="muted" data-testid="aq-demo-desc">{t.sewDemoHint}</small>
      <Bg t={t} white={white} setWhite={setWhite} />
      <button className="btn btn-accent aq-run" data-testid="aq-demo-create-btn" onClick={() => go({ kind: 'bozenSew', white })}><FolderPlus size={16} /> {t.aqDemoCreate}</button>
      <hr />
      <NetImport kind="sewer" />
    </>
  );
}

// demo & import tab: new project with a ready-made network (App handles 'giss-aqua-demo')
export function AquaDemo({ t, storm }) {
  const [kind, setKind] = useState('village');
  const [white, setWhite] = useState(!storm);
  if (storm) return <StormDemo t={t} white={white} setWhite={setWhite} />;
  const desc = { village: t.aqDemoGissHint, town: t.aqDemoTownHint, riser: t.aqDemoRiserHint, twotank: tr().demoHint, pumptank: tr().pdemoHint, boos: t.aqDemoBoosHint, bozenSew: t.sewDemoHint, bozen: t.aqDemoBozenHint, blank: t.aqDemoBlankHint, net1: t.aqDemoNet1Hint, net3: t.aqDemoNet3Hint }[kind];
  return (
    <>
      <p className="muted">{t.aqDemoIntro}</p>
      <b>{t.aqDemoWhich}</b>
      <Opt id="aq-demo-village" on={kind === 'village'} set={() => setKind('village')} label={t.aqDemoGiss} />
      <Opt id="aq-demo-town" on={kind === 'town'} set={() => setKind('town')} label={t.aqDemoTown} />
      <Opt id="aq-demo-boos" on={kind === 'boos'} set={() => setKind('boos')} label={t.aqDemoBoos} />
      <Opt id="aq-demo-bozen" on={kind === 'bozen'} set={() => setKind('bozen')} label={t.aqDemoBozen} />
      <Opt id="aq-demo-riser" on={kind === 'riser'} set={() => setKind('riser')} label={t.aqDemoRiser} />
      <Opt id="aq-demo-twotank" on={kind === 'twotank'} set={() => setKind('twotank')} label={tr().demo} />
      <Opt id="aq-demo-pumptank" on={kind === 'pumptank'} set={() => setKind('pumptank')} label={tr().pdemo} />
      <Opt id="aq-demo-blank" on={kind === 'blank'} set={() => setKind('blank')} label={t.aqDemoBlank} />
      <Opt id="aq-demo-net1" on={kind === 'net1'} set={() => setKind('net1')} label={t.aqDemoNet1} />
      <Opt id="aq-demo-net3" on={kind === 'net3'} set={() => setKind('net3')} label={t.aqDemoNet3} />
      <small className="muted" data-testid="aq-demo-desc">{desc}</small>
      <Bg t={t} white={white} setWhite={setWhite} />
      <button className="btn btn-accent aq-run" data-testid="aq-demo-create-btn" onClick={() => go({ kind, white })}><FolderPlus size={16} /> {t.aqDemoCreate}</button>
      <hr />
      <InpImport t={t} white={white} />
      <hr />
      <NetImport kind="water" />
    </>
  );
}
